import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { useVideoWatermark } from "@/hooks/use-video-watermark";
import { useAuthStore } from "@/stores/authStore";
import { LEAVE_FULLSCREEN_EVENT } from "@/lib/modal-focus";

type ProtectedPlayerFrameProps = {
  children: ReactNode;
  /** Shown over the video inside the frame (also in full screen), e.g. "Playing on another device". */
  overlay?: ReactNode;
};

type FullscreenVideo = HTMLVideoElement & { webkitExitFullscreen?: () => void };

/**
 * H3: wraps a lesson player (Bunny's iframe or a local <video>) with the moving watermark and
 * owns full screen, so the watermark is inside whatever fills the screen:
 *  - our "Full screen" button puts this wrapper in full screen (Fullscreen API), or — where a page
 *    element cannot go full screen (iPhone Safari) — fills the window with it instead;
 *  - if a child still goes full screen by itself (a browser's own video button), it is taken out
 *    and the wrapper goes full screen instead (or fills the window, if the browser refuses);
 *  - iPhone's native video full screen and picture-in-picture would show the bare video, so they
 *    are ended at once and the window-filling view is used.
 */
export function ProtectedPlayerFrame({ children, overlay }: ProtectedPlayerFrameProps) {
  const t = useTranslations("player");
  const frameRef = useRef<HTMLDivElement | null>(null);
  const email = useAuthStore((state) => state.user?.email ?? null);
  const [nativeFull, setNativeFull] = useState(false);
  const [windowFull, setWindowFull] = useState(false);
  useVideoWatermark(frameRef, email);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const onFullscreenChange = () => {
      const current = document.fullscreenElement;
      setNativeFull(current === frame);
      if (current && current !== frame && frame.contains(current)) {
        void document
          .exitFullscreen()
          .then(() => frame.requestFullscreen())
          .catch(() => setWindowFull(true));
      }
    };
    const onNativeVideoFullscreen = (event: Event) => {
      (event.target as FullscreenVideo).webkitExitFullscreen?.();
      setWindowFull(true);
    };
    const onPictureInPicture = () => {
      void document.exitPictureInPicture?.().catch(() => undefined);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    // Capture: these media events don't bubble.
    frame.addEventListener("webkitbeginfullscreen", onNativeVideoFullscreen, true);
    frame.addEventListener("enterpictureinpicture", onPictureInPicture, true);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      frame.removeEventListener("webkitbeginfullscreen", onNativeVideoFullscreen, true);
      frame.removeEventListener("enterpictureinpicture", onPictureInPicture, true);
    };
  }, []);

  // W-30: a dialog that must be seen (the idle prompt) takes the player out of the window-filling view.
  useEffect(() => {
    const onLeave = () => setWindowFull(false);
    window.addEventListener(LEAVE_FULLSCREEN_EVENT, onLeave);
    return () => window.removeEventListener(LEAVE_FULLSCREEN_EVENT, onLeave);
  }, []);

  useEffect(() => {
    if (!windowFull) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setWindowFull(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [windowFull]);

  const full = nativeFull || windowFull;

  function toggleFullscreen() {
    const frame = frameRef.current;
    if (!frame) return;
    if (document.fullscreenElement === frame) {
      void document.exitFullscreen().catch(() => undefined);
      return;
    }
    if (windowFull) {
      setWindowFull(false);
      return;
    }
    if (document.fullscreenEnabled && typeof frame.requestFullscreen === "function") {
      void frame.requestFullscreen().catch(() => setWindowFull(true));
    } else {
      setWindowFull(true);
    }
  }

  return (
    <div
      ref={frameRef}
      data-testid="protected-player"
      data-full={full ? (nativeFull ? "screen" : "window") : undefined}
      className={
        windowFull
          ? "fixed inset-0 z-[1000] flex items-center justify-center overflow-hidden bg-black"
          : nativeFull
            ? "relative flex h-full w-full items-center justify-center overflow-hidden bg-black"
            : "relative overflow-hidden rounded-lg"
      }
    >
      {children}
      {overlay}
      <button
        type="button"
        onClick={toggleFullscreen}
        className="absolute right-2 top-2 z-10 rounded-md bg-black/60 px-2 py-1 text-xs font-medium text-white hover:bg-black/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
        data-testid="player-fullscreen"
      >
        {full ? t("exitFullscreen") : t("fullscreen")}
      </button>
    </div>
  );
}
